import { useState, useEffect } from 'react';
import api, { getList } from '../../api/client';
import { todayISO, currentMonthISO, daysUntilAnniversary, ordinal, relativeDayLabel } from '../../utils/format';
import { liveOnly } from '../../utils/records';
import { isGuest, guestPhase } from '../members/guest';
import type {
  DashboardSnapshot, UpcomingCelebration, Student, ChurchEvent,
  Donation, Collection, Expense, Budget, BudgetTransaction, Member, Family, Numeric
} from '../../types';

const EMPTY: DashboardSnapshot = {
  familyCount: 0, memberCount: 0, guestCount: 0, guestsEndingSoon: 0, studentCount: 0,
  todayEvents: [], upcomingBirthdays: [],
  mIncome: 0, mExpense: 0, utilization: 0,
  isSyncing: true
};

// One dead endpoint must not blank the whole dashboard.
const safe = (path: string): Promise<unknown> => api.get(path).catch(() => null);

// The Promise.all aggregation that used to live inline in Dashboard.jsx.
export const useDashboardData = (enabled = true): DashboardSnapshot => {
  const [data, setData] = useState<DashboardSnapshot>(EMPTY);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    setData((prev) => ({ ...prev, isSyncing: true }));

    Promise.all([
      safe('/api/families'),
      safe('/api/members'),
      safe('/api/students'),
      safe('/api/events'),
      safe('/api/donations'),
      safe('/api/collections'),
      safe('/api/expenses'),
      safe('/api/budgets'),
      safe('/api/budget-transactions')
    ])
      .then(([families, members, students, events, donations, collections, expenses, budgets, bgtTxns]) => {
        if (cancelled) return;

        const todayStr = todayISO();
        const currentMonthStr = currentMonthISO();

        // --- Headcounts ---
        // Soft-deleted records still come back from the API, so they are
        // filtered out here rather than counted. getTotal() cannot be used for
        // the same reason: its totalElements includes them.
        const liveMembers = liveOnly(getList<Member>(members));
        const familyCount = liveOnly(getList<Family>(families)).length;
        // Guests are attending for a period, not on the roll, so they are
        // counted apart from the members — and flagged when the stay is
        // ending within 30 days.
        const guests = liveMembers.filter((m) => isGuest(m)
          && !['Deceased', 'Transferred', 'Inactive'].includes(String(m.status || '')));
        const memberCount = liveMembers.length - liveMembers.filter((m) => isGuest(m)).length;
        const guestCount = guests.length;
        const guestsEndingSoon = guests.filter((m) => guestPhase(m) === 'expiring').length;
        const studentCount = getList<Student>(students).filter((s) => s.status === 'ACTIVE').length;

        // --- Today's activities ---
        const todayEvents = getList<ChurchEvent>(events)
          .filter((e) => (e.date || e.startDate || e.eventDate) === todayStr)
          .map((e) => ({
            time: e.time || e.startTime || 'All Day',
            title: e.name || e.title || e.eventName || 'Unnamed Event',
            sortTime: e.time || e.startTime || '23:59'
          }))
          .sort((a, b) => a.sortTime.localeCompare(b.sortTime))
          .slice(0, 4);

        if (todayEvents.length === 0) {
          todayEvents.push({ time: '-', title: 'No events or meetings scheduled for today.', sortTime: '99:99' });
        }

        // --- Finance ---
        let mIncome = 0;
        let mExpense = 0;
        let totalAnnBudget = 0;
        let totalAnnExpense = 0;

        const processFinance = (
          dateStr: string | undefined,
          amt: Numeric | undefined,
          type: string | undefined
        ) => {
          if (!dateStr || !amt) return;
          const numAmt = parseFloat(String(amt)) || 0;
          if (type === 'Income' && dateStr.startsWith(currentMonthStr)) mIncome += numAmt;
          if (type === 'Expense') {
            totalAnnExpense += numAmt;
            if (dateStr.startsWith(currentMonthStr)) mExpense += numAmt;
          }
        };

        getList<Donation>(donations).forEach((d) => { if (d.status === 'ACTIVE') processFinance(d.date, d.amount, 'Income'); });
        getList<Collection>(collections).forEach((c) => { if (c.status === 'ACTIVE') processFinance(c.date, c.amount, 'Income'); });
        getList<Expense>(expenses).forEach((e) => { if (e.status === 'ACTIVE') processFinance(e.date, e.amount, 'Expense'); });
        getList<BudgetTransaction>(bgtTxns).forEach((t) => { if (t.status === 'ACTIVE') processFinance(t.date, t.amount, t.type); });

        getList<Budget>(budgets).forEach((b) => {
          if (b.headType === 'Expense' && b.status === 'ACTIVE' && b.workflowStatus === 'Approved') {
            totalAnnBudget += parseFloat(String(b.amount ?? 0)) || 0;
          }
        });

        const utilization = totalAnnBudget > 0
          ? Math.min(100, Math.round((totalAnnExpense / totalAnnBudget) * 100))
          : 0;

        // --- Birthdays & anniversaries in the next 7 days ---
        const celebrations: UpcomingCelebration[] = [];
        liveMembers.forEach((m) => {
          const add = (dateStr: string | undefined, isAnniversary: boolean) => {
            const next = daysUntilAnniversary(dateStr);
            if (!next || next.days < 0 || next.days > 7) return;
            celebrations.push({
              name: `${m.name || 'Member'}${isAnniversary ? ' (Anniv)' : ''}`,
              date: relativeDayLabel(next.days),
              desc: isAnniversary ? `${ordinal(next.years)} Anniv.` : `Turning ${next.years}`,
              daysAway: next.days
            });
          };
          add(m.dateOfBirth, false);
          add(m.dateOfMarriage, true);
        });

        celebrations.sort((a, b) => a.daysAway - b.daysAway);
        const upcomingBirthdays = celebrations.slice(0, 4);
        if (upcomingBirthdays.length === 0) {
          upcomingBirthdays.push({ name: 'No celebrations this week.', date: '', desc: '', daysAway: 99 });
        }

        setData({
          familyCount, memberCount, guestCount, guestsEndingSoon, studentCount,
          todayEvents, upcomingBirthdays,
          mIncome, mExpense, utilization,
          isSyncing: false
        });
      })
      .catch(() => {
        if (!cancelled) setData((prev) => ({ ...prev, isSyncing: false }));
      });

    return () => { cancelled = true; };
  }, [enabled]);

  return data;
};

export default useDashboardData;
